/**
 * Where the problem report opens from (bata-platform#686, ADR 9063).
 *
 * One drawer, mounted once above the chrome, opened from two places that are far apart in the
 * tree: «Report a problem» in the identity menu (and in the collapsed rail's copy of it) and the
 * crash screen's own button. A context carries the `open` function to both; the drawer itself
 * lives here so there is one form, one state and one focus return, however it was opened.
 *
 * A person who is not inside a provider — a test that mounts the identity menu alone — gets
 * `null` from {@link useFeedback}, and the menu draws no entry: an entry that opens nothing is
 * worse than none.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Drawer } from '../../primitives';

import { FeedbackForm, type FeedbackPrefill } from './FeedbackForm';
import { screenScope } from './screen-scope';

export interface FeedbackControls {
  /** Opens the report form, optionally pre-filled (the crash screen's error message, OPTIMA's draft). */
  readonly open: (prefill?: FeedbackPrefill) => void;
  /**
   * Opens the form from an entry of the identity menu. The menu closes with it, and Radix would
   * give its trigger the focus back — outside the drawer, which closes a non-modal dialog the
   * moment focus lands elsewhere. The menu's content passes {@link keepFocusInForm} as its
   * `onCloseAutoFocus` so that one return is skipped and the form keeps the focus.
   */
  readonly openFromMenu: () => void;
  /** For a menu's `onCloseAutoFocus`: skips the focus return the menu owes when it closed to open the form. */
  readonly keepFocusInForm: (event: Event) => void;
}

const FeedbackContext = createContext<FeedbackControls | null>(null);

/** The report form's controls, or `null` where no provider is mounted. */
export function useFeedback(): FeedbackControls | null {
  return useContext(FeedbackContext);
}

export interface FeedbackProviderProps {
  /** The path the person is on — the route the report is sent with. */
  readonly currentPath: string;
  /** The address's parameters, for the market and the run a report can carry (ADR 9071). */
  readonly params?: Readonly<Record<string, string>>;
  readonly children: React.ReactNode;
}

interface Session {
  /** A new one on every opening, so a form never carries yesterday's draft into a new report. */
  readonly id: number;
  readonly prefill: FeedbackPrefill;
}

export function FeedbackProvider({ currentPath, params, children }: FeedbackProviderProps): React.JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const fromMenu = useRef(false);

  const open = useCallback((prefill: FeedbackPrefill = {}) => {
    setSession((previous) => ({ id: (previous?.id ?? 0) + 1, prefill }));
  }, []);
  const openFromMenu = useCallback(() => {
    fromMenu.current = true;
    open();
  }, [open]);
  const keepFocusInForm = useCallback((event: Event) => {
    // Consumed by the one close the form was opened from; any other close of a menu returns focus as usual.
    if (!fromMenu.current) return;
    fromMenu.current = false;
    event.preventDefault();
  }, []);
  const controls = useMemo<FeedbackControls>(() => ({ open, openFromMenu, keepFocusInForm }), [open, openFromMenu, keepFocusInForm]);

  return (
    <FeedbackContext.Provider value={controls}>
      {children}
      <Drawer open={session !== null} onOpenChange={(next) => { if (!next) setSession(null); }} titleKey="feedback.report.title">
        {session !== null && (
          <FeedbackForm key={session.id} route={currentPath} scope={screenScope(params)} prefill={session.prefill} onSent={() => setSession(null)} />
        )}
      </Drawer>
    </FeedbackContext.Provider>
  );
}
